import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Grid, Header, Message, Tab,
} from 'semantic-ui-react';
import { graphql } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';

import { isEmpty } from 'lodash';
import client from '../apollo';

import AuctionExceptions from '../views/ExpiredVraSettings/AuctionExceptions';
import DealerExceptions from '../views/ExpiredVraSettings/DealerExceptions';
import GroupExceptions from '../views/ExpiredVraSettings/GroupExceptions';

import GlobalPeriodEdit from '../views/ExpiredVraSettings/ModalForm/Edit';

import autoCancellationExceptionListQuery from '../graphql/autoCancellationExceptionListQuery';
import updateAutoCancellationExceptionMutation from '../graphql/updateAutoCancellationExceptionMutation';

import LoadingDataEnhancer from './LoadingData';

const ExpiredVraSettingsTabs = props => {
  const { updateMutation } = props;

  const [selectedGlobalGracePeriod, setSelectedGlobalGracePeriod] = useState({});
  const [selectedGlobalReinstateGracePeriod, setSelectedGlobalReinstateGracePeriod] = useState({});
  const [isEditGlobalPeriodModalOpen, setIsEditGlobalPeriodModalOpen] = useState(false);
  const [isEditGlobalReinstatePeriodModalOpen, setIsEditGlobalReinstatePeriodModalOpen] = useState(false);
  const [editGlobalPeriodFormError, setEditGlobalPeriodFormError] = useState(null);
  const [editGlobalPeriodFormSubmitting, setEditGlobalPeriodFormSubmitting] = useState(false);
  const [editGlobalReinstatePeriodFormError, setEditGlobalReinstatePeriodFormError] = useState(null);
  const [editGlobalReinstatePeriodFormSubmitting, setEditGlobalReinstatePeriodFormSubmitting] = useState(false);
  const [flashMessage, setFlashMessage] = useState(null);

  const findAPeriodByType = useCallback((periods, type) => (
    periods.find(period => period.exceptionType === type) || null
  ), []);

  const fetchAllGlobalReturn = useCallback(() => {
    client
      .query({
        query: autoCancellationExceptionListQuery,
        variables: {
          exceptionTypes: 'GLOBAL_RETURN,GLOBAL_REINSTATED_RETURN',
        },
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        const { autoCancellationExceptionList } = data;
        if (!isEmpty(autoCancellationExceptionList)) {
          const globalGracePeriod = findAPeriodByType(
            autoCancellationExceptionList,
            'GLOBAL_RETURN'
          );
          if (isEmpty(globalGracePeriod)) {
            setFlashMessage(null);
            setEditGlobalPeriodFormError('Not Found Global Period.');
          }

          const globalGraceReinstatedPeriod = findAPeriodByType(
            autoCancellationExceptionList,
            'GLOBAL_REINSTATED_RETURN'
          );
          if (isEmpty(globalGraceReinstatedPeriod)) {
            setFlashMessage(null);
            setEditGlobalReinstatePeriodFormError('Not Found Global Reinstated Period.');
          }

          setSelectedGlobalGracePeriod(globalGracePeriod || {});
          setSelectedGlobalReinstateGracePeriod(globalGraceReinstatedPeriod || {});
        }
      })
      .catch(() => {
        setFlashMessage(null);
        setEditGlobalPeriodFormError('Retrieve Global Period Failed.');
        setEditGlobalReinstatePeriodFormError('Retrieve Global Reinstated Period Failed.');
      });
  }, [findAPeriodByType]);

  useEffect(() => {
    fetchAllGlobalReturn();
  }, [fetchAllGlobalReturn]);

  const editGlobalGracePeriodHandler = useCallback(() => {
    setIsEditGlobalPeriodModalOpen(true);
  }, []);

  const editGlobalReinstatePeriodHandler = useCallback(() => {
    setIsEditGlobalReinstatePeriodModalOpen(true);
  }, []);

  const onGlobalPeriodEditFormCloseHandler = useCallback(() => {
    setEditGlobalPeriodFormError(null);
    setEditGlobalPeriodFormSubmitting(false);
    setIsEditGlobalPeriodModalOpen(false);
  }, []);

  const onGlobalReinstatePeriodEditFormCloseHandler = useCallback(() => {
    setEditGlobalReinstatePeriodFormError(null);
    setEditGlobalReinstatePeriodFormSubmitting(false);
    setIsEditGlobalReinstatePeriodModalOpen(false);
  }, []);

  const onGlobalPeriodEditFormSubmitHandler = useCallback(values => {
    const input = { ...selectedGlobalGracePeriod, ...values };

    setEditGlobalPeriodFormSubmitting(true);
    setEditGlobalPeriodFormError(null);

    updateMutation({
      variables: { ...input },
    })
      .then(({ data }) => {
        const { updateAutoCancellationException: { success } } = data;
        if (success) {
          setEditGlobalPeriodFormSubmitting(false);
          setFlashMessage('Successfully Updated Global Grace Period.');
          setEditGlobalPeriodFormError(null);
          onGlobalPeriodEditFormCloseHandler();
          fetchAllGlobalReturn();
        } else {
          setEditGlobalPeriodFormSubmitting(false);
          setFlashMessage(null);
          setEditGlobalPeriodFormError('Failed To Update Global Grace Period.');
        }
      })
      .catch(() => {
        setEditGlobalPeriodFormSubmitting(false);
        setFlashMessage(null);
        setEditGlobalPeriodFormError('Failed To Update Global Grace Period.');
      });
  }, [selectedGlobalGracePeriod, updateMutation, onGlobalPeriodEditFormCloseHandler, fetchAllGlobalReturn]);

  const onGlobalReinstatePeriodEditFormSubmitHandler = useCallback(values => {
    const input = {
      ...selectedGlobalReinstateGracePeriod,
      ...values,
    };

    setEditGlobalReinstatePeriodFormSubmitting(true);
    setEditGlobalReinstatePeriodFormError(null);

    updateMutation({
      variables: { ...input },
    })
      .then(({ data }) => {
        const { updateAutoCancellationException: { success } } = data;
        if (success) {
          setEditGlobalReinstatePeriodFormSubmitting(false);
          setFlashMessage('Successfully Updated Global Reinstated Grace Period.');
          onGlobalReinstatePeriodEditFormCloseHandler();
          fetchAllGlobalReturn();
        } else {
          setEditGlobalReinstatePeriodFormError('Failed To Update Global Reinstated Period.');
          setFlashMessage(null);
          setEditGlobalReinstatePeriodFormSubmitting(false);
        }
      })
      .catch(() => {
        setEditGlobalReinstatePeriodFormSubmitting(false);
        setFlashMessage(null);
        setEditGlobalReinstatePeriodFormError('Failed To Update Global Reinstated Period.');
      });
  }, [selectedGlobalReinstateGracePeriod, updateMutation, onGlobalReinstatePeriodEditFormCloseHandler, fetchAllGlobalReturn]);

  const handleDismiss = useCallback(() => {
    setFlashMessage(null);
  }, []);

  const panes = [
    {
      menuItem: 'Auction Exceptions',
      render: () => (
        <Tab.Pane>
          {' '}
          <AuctionExceptions />
        </Tab.Pane>
      ),
    },
    {
      menuItem: 'Dealer Exceptions',
      render: () => (
        <Tab.Pane>
          <DealerExceptions />
        </Tab.Pane>
      ),
    },
    {
      menuItem: 'Group Exceptions',
      render: () => (
        <Tab.Pane>
          <GroupExceptions />
        </Tab.Pane>
      ),
    },
  ];

  return (
    <div>
      {flashMessage && (
        <Message onDismiss={handleDismiss} content={flashMessage} positive />
      )}
      <Grid>
        <Grid.Row>
          <Grid.Column>
            <Header size='large'>Expired VRA Settings</Header>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={4}>
            <Header size='tiny'>Global Grace Period:</Header>
          </Grid.Column>
          <Grid.Column width={2}>
            {!isEmpty(selectedGlobalGracePeriod)
              && selectedGlobalGracePeriod.gracePeriod}{' '}
            days
          </Grid.Column>
          <Grid.Column width={4}>
            <Button
              color='green'
              onClick={editGlobalGracePeriodHandler}
              inverted
            >
              Edit Grace Period
            </Button>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={4}>
            <Header size='tiny'>Global Reinstated VRA Grace Period:</Header>
          </Grid.Column>
          <Grid.Column width={2}>
            {!isEmpty(selectedGlobalReinstateGracePeriod)
              && selectedGlobalReinstateGracePeriod.gracePeriod}{' '}
            days
          </Grid.Column>
          <Grid.Column width={4}>
            <Button
              color='green'
              onClick={editGlobalReinstatePeriodHandler}
              inverted
            >
              Edit Grace Period
            </Button>
          </Grid.Column>
        </Grid.Row>
      </Grid>{' '}
      {isEditGlobalPeriodModalOpen && (
        <div>
          <GlobalPeriodEdit
            modalType='UPDATE_GLOBAL_PERIOD'
            onCloseHandler={onGlobalPeriodEditFormCloseHandler}
            onSubmitHandler={onGlobalPeriodEditFormSubmitHandler}
            error={editGlobalPeriodFormError}
            submitting={editGlobalPeriodFormSubmitting}
            globalExpiredVraSettings={selectedGlobalGracePeriod}
          />
        </div>
      )}
      {isEditGlobalReinstatePeriodModalOpen && (
        <div>
          <GlobalPeriodEdit
            modalType='UPDATE_GLOBAL_REINSTATE_PERIOD'
            onCloseHandler={onGlobalReinstatePeriodEditFormCloseHandler}
            onSubmitHandler={
              onGlobalReinstatePeriodEditFormSubmitHandler
            }
            error={editGlobalReinstatePeriodFormError}
            submitting={editGlobalReinstatePeriodFormSubmitting}
            globalExpiredVraSettings={selectedGlobalReinstateGracePeriod}
          />
        </div>
      )}
      <Tab panes={panes} />
    </div>
  );
};

ExpiredVraSettingsTabs.propTypes = {
  updateMutation: PropTypes.func.isRequired,
};

export default compose(
  withRouter,
  graphql(updateAutoCancellationExceptionMutation, { name: 'updateMutation' }),
  LoadingDataEnhancer
)(ExpiredVraSettingsTabs);
